import React, { useState, useEffect, useRef, useMemo } from 'react';
import { apiCall } from '../../../../_services/apiCall';
import config from '../../../../config/config';

const API = config.POSTLOGIN_API_URL_COMPANY;

// ─── Pure date / period helpers ───────────────────────────────────────────────

export const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Appending T00:00:00 avoids off-by-one-day bug with Date("YYYY-MM-DD") in some browsers.
const localDate = dateStr => new Date(dateStr + 'T00:00:00');

// Returns { key, label } for a date + aggregation. key is used for deduplication;
// label is shown in chart headers. Both must stay in sync with backend timePeriods[].period.
const getPeriod = (date, agg, queryFromDate) => {
  const d = localDate(date);
  switch (agg) {
    case 'quarterly': {
      const qStart = queryFromDate ? localDate(queryFromDate) : new Date(d.getFullYear(), 0, 1);
      const qNum   = Math.floor(((d.getFullYear() - qStart.getFullYear()) * 12 + (d.getMonth() - qStart.getMonth())) / 3) + 1;
      const fy     = qStart.getFullYear();
      return { key: `FY${fy}-Q${qNum}`, label: `Q${qNum} ${fy}-${String(fy + 1).slice(2)}` };
    }
    case 'half-yearly': {
      const qStart        = queryFromDate ? localDate(queryFromDate) : new Date(d.getFullYear(), 0, 1);
      const monthsElapsed = (d.getFullYear() - qStart.getFullYear()) * 12 + (d.getMonth() - qStart.getMonth());
      const halfIndex     = Math.floor(monthsElapsed / 6);
      const hNum          = (halfIndex % 2) + 1;
      const fyYear        = qStart.getFullYear() + Math.floor(halfIndex / 2);
      const h             = `H${hNum}`;
      return { key: `${h}-FY${fyYear}`, label: `${h} ${fyYear}-${String(fyYear + 1).slice(2)}` };
    }
    case 'weekly': {
      const wStart  = queryFromDate ? localDate(queryFromDate) : new Date(d.getFullYear(), 0, 1);
      const weekNum = Math.floor(Math.round((d.getTime() - wStart.getTime()) / 86400000) / 7) + 1;
      const w       = `W${String(weekNum).padStart(2, '0')}`;
      return { key: w, label: w };
    }
    case 'daily': {
      const iso = date.slice(0, 10);
      return { key: iso, label: `${String(d.getDate()).padStart(2, '0')} ${MONTHS_SHORT[d.getMonth()]}` };
    }
    default: {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return { key, label: `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}` };
    }
  }
};

// Filters a periods array based on the user's period-range selectors (null = show all).
export const filterPeriods = (periods, aggregation, selectedWeekMonths, selectedQuarters, selectedHalves) => {
  if (aggregation === 'weekly' && selectedWeekMonths?.length > 0)
    return periods.filter(p => selectedWeekMonths.includes(new Date(p.fromDate + 'T00:00:00').getMonth()));
  if (aggregation === 'quarterly' && selectedQuarters?.length > 0)
    return periods.filter(p => { const m = p.label.match(/Q(\d)/); return m && selectedQuarters.includes(parseInt(m[1])); });
  if (aggregation === 'half-yearly' && selectedHalves?.length > 0)
    return periods.filter(p => selectedHalves.includes(p.label.slice(0, 2)));
  return periods;
};

// Builds ordered [{ key, label, fromDate }] for a date range + aggregation.
// Steps month-by-month (or day/week), deduplicates by bucket key so quarterly/half-yearly
// always produce exactly 4 / 2 entries per FY.
export const generatePeriods = (fromDate, toDate, agg = 'monthly') => {
  if (!fromDate || !toDate) return [];
  const map = new Map();
  const end = new Date(toDate + 'T00:00:00');
  const cur = new Date(fromDate + 'T00:00:00');

  while (cur <= end) {
    const iso           = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
    const { key, label } = getPeriod(iso, agg, fromDate);
    if (!map.has(key)) map.set(key, { label, fromDate: iso, key });
    if      (agg === 'daily')  cur.setDate(cur.getDate() + 1);
    else if (agg === 'weekly') cur.setDate(cur.getDate() + 7);
    else                       { cur.setMonth(cur.getMonth() + 1); cur.setDate(1); }
  }
  return Array.from(map.values());
};

// Converts a FY object to calendar start/end dates. startingMonth=4 → Apr FY start.
export const fyDateRange = (fy, startingMonth = 4) => {
  if (!fy) return { fromDate: '', toDate: '' };
  const parts     = (fy.financial_year_value || '').split('-');
  const startYear = parseInt(parts[0], 10);
  if (isNaN(startYear)) return { fromDate: '', toDate: '' };
  const fromDate = `${startYear}-${String(startingMonth).padStart(2, '0')}-01`;
  const endYear  = startingMonth === 1 ? startYear : startYear + 1;
  const endMonth = startingMonth === 1 ? 12 : startingMonth - 1;
  const lastDay  = new Date(endYear, endMonth, 0).getDate();
  return { fromDate, toDate: `${endYear}-${String(endMonth).padStart(2, '0')}-${lastDay}` };
};

// Human-readable label for an intensity config based on its groupByField.
export const cfgLabel = cfg => {
  if (!cfg) return '';
  switch (cfg.groupByField) {
    case 'subCategory': return cfg.subCategories?.[0] || cfg.category || cfg.module || `Config #${cfg.id}`;
    case 'kpi':         return cfg.kpiIds?.[0]        || cfg.category || cfg.module || `Config #${cfg.id}`;
    case 'category':    return cfg.category           || cfg.module   || `Config #${cfg.id}`;
    default:            return cfg.module             || `Config #${cfg.id}`;
  }
};

// ─── Reusable UI components ───────────────────────────────────────────────────

export function LocationMultiSelect({ locations, selected, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const locLabel  = l => l.unitCode || l.location?.city || `Loc ${l.id}`;
  const filtered  = locations.filter(l => locLabel(l).toLowerCase().includes(search.toLowerCase()));
  const allSel    = locations.length > 0 && selected.length === locations.length;
  const toggle    = id => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  const toggleAll = () => onChange(allSel ? [] : locations.map(l => l.id));
  const display   = selected.length === 0 ? 'None' : allSel ? `All (${locations.length})` : `${selected.length} / ${locations.length}`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={S.filterLabel}><span style={{ color: '#3b82f6', marginRight: 4 }}>●</span>Location *</div>
      <div style={{ ...S.trigger, minWidth: 200, borderColor: open ? '#3b82f6' : '#e5e7eb' }} onClick={() => setOpen(o => !o)}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
        <span style={{ fontSize: 9, color: '#9ca3af', marginLeft: 6, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && locations.length > 0 && (
        <div style={S.dropdown}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>
            <input autoFocus type="text" placeholder="Search location..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', border: '1.5px solid #3b82f6', borderRadius: 6, padding: '5px 8px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={S.dropdownAll} onClick={toggleAll}>
            <input type="checkbox" readOnly checked={allSel} /><span style={{ fontWeight: 600 }}>Select All</span>
          </div>
          {filtered.map(l => {
            const checked = selected.includes(l.id);
            return (
              <div key={l.id} style={{ ...S.dropdownOpt, background: checked ? '#eff6ff' : 'transparent' }} onClick={() => toggle(l.id)}>
                <input type="checkbox" readOnly checked={checked} /><span>{locLabel(l)}</span>
              </div>
            );
          })}
          {filtered.length === 0 && <div style={{ padding: '10px 12px', fontSize: 12, color: '#9ca3af' }}>No locations found</div>}
        </div>
      )}
    </div>
  );
}

// Generic multi-select dropdown. Supports optional grouping via option.group field.
export function MultiSelect({ label, options, selected, onChange, getId, getLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const allSel    = options.length > 0 && selected.length === options.length;
  const toggle    = id => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  const toggleAll = () => onChange(allSel ? [] : options.map(o => getId(o)));
  const display   = selected.length === 0 ? 'None' : allSel ? `All (${options.length})` : `${selected.length} / ${options.length}`;

  const hasGroups = options.some(o => o.group);
  const grouped   = hasGroups
    ? options.reduce((acc, opt) => { const g = opt.group || ''; if (!acc[g]) acc[g] = []; acc[g].push(opt); return acc; }, {})
    : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={S.filterLabel}>{label}</div>
      <div style={{ ...S.trigger, borderColor: open ? '#3b82f6' : '#e5e7eb' }} onClick={() => setOpen(o => !o)}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{display}</span>
        <span style={{ fontSize: 9, color: '#9ca3af', marginLeft: 6, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && options.length > 0 && (
        <div style={S.dropdown}>
          <div style={S.dropdownAll} onClick={toggleAll}>
            <input type="checkbox" readOnly checked={allSel} /><span style={{ fontWeight: 600 }}>Select All</span>
          </div>
          {hasGroups
            ? Object.entries(grouped).map(([group, groupOpts]) => (
                <React.Fragment key={group}>
                  <div style={{ padding: '5px 12px 3px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f1f5f9', borderTop: '1px solid #e5e7eb' }}>
                    {group}
                  </div>
                  {groupOpts.map(opt => {
                    const id      = getId(opt);
                    const checked = selected.includes(id);
                    return (
                      <div key={id} style={{ ...S.dropdownOpt, paddingLeft: 20, background: checked ? '#eff6ff' : 'transparent' }} onClick={() => toggle(id)}>
                        <input type="checkbox" readOnly checked={checked} /><span>{getLabel(opt)}</span>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))
            : options.map(opt => {
                const id      = getId(opt);
                const checked = selected.includes(id);
                return (
                  <div key={id} style={{ ...S.dropdownOpt, background: checked ? '#eff6ff' : 'transparent' }} onClick={() => toggle(id)}>
                    <input type="checkbox" readOnly checked={checked} /><span>{getLabel(opt)}</span>
                  </div>
                );
              })
          }
        </div>
      )}
    </div>
  );
}

// ─── useIntensityData — main data hook ────────────────────────────────────────

// Central data hook shared by Quick View and Dashboard pages.
// Fetches numerator (intensity/data) and denominator (intensity/denominators) in parallel.
export function useIntensityData({ fromDate, toDate, aggregation, groupByLocation = false }) {
  const [locations,         setLocations]         = useState([]);
  const [selectedLocIds,    setSelectedLocIds]    = useState([]);
  const [allConfigs,        setAllConfigs]        = useState([]);
  const [selectedConfigIds, setSelectedConfigIds] = useState([]);
  const [selectedKpis,      setSelectedKpis]      = useState([]);
  const [intensityData,     setIntensityData]     = useState([]);
  const [denomData,         setDenomData]         = useState([]);
  const [selectedDenomIds,  setSelectedDenomIds]  = useState([]);
  const [loading,           setLoading]           = useState(false);

  useEffect(() => {
    (async () => {
      const res  = await apiCall(`${API}getSource`, {}, {}, 'GET');
      const locs = res?.data?.data || [];
      setLocations(locs);
      setSelectedLocIds(locs.map(l => l.id));
    })();
    (async () => {
      const res  = await apiCall(`${API}intensity/config`, {}, {}, 'GET');
      const cfgs = res?.data?.data || [];
      setAllConfigs(cfgs);
      setSelectedConfigIds(cfgs.map(c => c.id));
      setSelectedKpis(extractKpiOptions(cfgs).map(o => o.id));
    })();
  }, []); // eslint-disable-line

  const extractKpiOptions = cfgs => {
    const seen = new Set();
    return cfgs.flatMap(c => {
      const group = cfgLabel(c);
      return (c.kpiLevel?.kpiIds || []).filter(k => !seen.has(k) && seen.add(k)).map(k => ({ id: k, label: k, group }));
    });
  };

  const kpiOptions = useMemo(() => extractKpiOptions(allConfigs), [allConfigs]); // eslint-disable-line

  useEffect(() => { setSelectedKpis(kpiOptions.map(o => o.id)); }, [kpiOptions.map(o => o.id).join(',')]); // eslint-disable-line

  useEffect(() => {
    const titles = denomData.map(d => d.title);
    setSelectedDenomIds(prev => {
      const kept  = prev.filter(id => titles.includes(id));
      const added = titles.filter(id => !prev.includes(id));
      return [...kept, ...added];
    });
  }, [denomData.map(d => d.title).join(',')]); // eslint-disable-line

  const filteredDenomData = useMemo(
    () => denomData.filter(d => selectedDenomIds.includes(d.title)),
    [denomData, selectedDenomIds], // eslint-disable-line
  );

  // Fetch data for selected configs + any config whose KPI the user has selected.
  const fetchConfigIds = useMemo(() => {
    const kpiConfigIds = allConfigs
      .filter(c => (c.kpiLevel?.kpiIds || []).some(k => selectedKpis.includes(k)))
      .map(c => c.id);
    return [...new Set([...selectedConfigIds, ...kpiConfigIds])];
  }, [allConfigs, selectedConfigIds, selectedKpis]); // eslint-disable-line

  const fetchData = async () => {
    if (!fromDate || !toDate || !fetchConfigIds.length || !selectedLocIds.length) return;
    setLoading(true);
    try {
      const numP = new URLSearchParams();
      fetchConfigIds.forEach(id => numP.append('configIds', id));
      numP.set('locationIds', selectedLocIds.join(','));
      numP.set('fromDate', fromDate);
      numP.set('toDate', toDate);
      numP.set('aggregation', aggregation);
      if (groupByLocation) numP.set('groupByLocation', 'true');

      const denP = new URLSearchParams({
        locationIds: selectedLocIds.join(','),
        fromDate, toDate, aggregation,
        ...(groupByLocation && { groupByLocation: 'true' }),
      });

      const [numRes, denRes] = await Promise.all([
        apiCall(`${API}intensity/data?${numP}`, {}, {}, 'GET'),
        apiCall(`${API}intensity/denominators?${denP}`, {}, {}, 'GET'),
      ]);
      setIntensityData(numRes?.data?.data || []);
      setDenomData(denRes?.data?.data || []);
    } catch (e) {
      console.error('[useIntensityData] fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLocIds.length === 0) { setIntensityData([]); setDenomData([]); return; }
    if (!fromDate || !toDate || !fetchConfigIds.length) return;
    fetchData(); // eslint-disable-line
  }, [fromDate, toDate, aggregation, groupByLocation, fetchConfigIds.join(','), selectedLocIds.join(',')]); // eslint-disable-line

  // Groups API rows by configId into { topRows, kpiRows } for rendering.
  const tableGroups = useMemo(() => {
    const relevantIds = [...new Set([
      ...selectedConfigIds,
      ...allConfigs.filter(c => (c.kpiLevel?.kpiIds || []).some(k => selectedKpis.includes(k))).map(c => c.id),
    ])];
    return relevantIds
      .map(configId => {
        const cfg     = allConfigs.find(c => c.id === configId);
        if (!cfg) return null;
        const topRows = selectedConfigIds.includes(configId)
          ? intensityData.filter(r => r.configId === configId && r.type === 'topLevel')
          : [];
        const kpiRows = intensityData
          .filter(r => r.configId === configId && r.type === 'kpiLevel')
          .filter(r => selectedKpis.includes(r.label));
        return { configId, cfg, topRows, kpiRows };
      })
      .filter(Boolean)
      .filter(g => g.topRows.length > 0 || g.kpiRows.length > 0);
  }, [intensityData, selectedConfigIds, allConfigs, selectedKpis]); // eslint-disable-line

  return {
    locations, selectedLocIds, setSelectedLocIds,
    allConfigs, selectedConfigIds, setSelectedConfigIds,
    kpiOptions, selectedKpis, setSelectedKpis,
    intensityData, denomData, filteredDenomData, selectedDenomIds, setSelectedDenomIds,
    loading, fetchData,
    tableGroups, fetchConfigIds,
  };
}

// ─── Shared style tokens ──────────────────────────────────────────────────────

export const S = {
  filterCard:         { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '18px 20px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  filterCardTitle:    { fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 2 },
  filterCardSubtitle: { fontSize: 12, color: '#9ca3af', marginBottom: 14 },
  filtersRow:         { display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 },
  filterLabel:        { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'flex', alignItems: 'center' },
  statBadge:          { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.15)' },
  trigger:            { display: 'flex', alignItems: 'center', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '5px 10px', background: '#fafafa', cursor: 'pointer', minWidth: 140, height: 34, fontSize: 13, color: '#374151', userSelect: 'none' },
  dropdown:           { position: 'absolute', top: 'calc(100% + 4px)', left: 0, minWidth: '100%', maxWidth: 300, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 999, maxHeight: 240, overflowY: 'auto' },
  dropdownAll:        { padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', borderBottom: '1px solid #f3f4f6', background: '#f9fafb', fontSize: 13, color: '#374151' },
  dropdownOpt:        { padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#374151' },
  select:             { border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '5px 10px', fontSize: 13, height: 34, background: '#fafafa', color: '#374151', cursor: 'pointer' },
  btnRefresh:         { height: 34, padding: '0 14px', border: '1.5px solid #e5e7eb', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 500 },
  spinner:            { width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#2b7a9e', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
};

// ─── useIntensityFilters — date range + aggregation state ─────────────────────

// Hard caps to prevent fetching too much data at once in daily/weekly modes.
const AGG_MAX_DAYS = { daily: 30, weekly: 84 };
const getToday = () => new Date().toISOString().slice(0, 10);

export const MONTHS = [
  { label: 'April',    value: 3  }, { label: 'May',       value: 4  },
  { label: 'June',     value: 5  }, { label: 'July',      value: 6  },
  { label: 'August',   value: 7  }, { label: 'September', value: 8  },
  { label: 'October',  value: 9  }, { label: 'November',  value: 10 },
  { label: 'December', value: 11 }, { label: 'January',   value: 0  },
  { label: 'February', value: 1  }, { label: 'March',     value: 2  },
];

export function useIntensityFilters() {
  const [fromDate,      setFromDate]      = useState('');
  const [toDate,        setToDate]        = useState('');
  const [aggregation,   setAggregation]   = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(null);

  const effectiveDates = useMemo(() => {
    if (aggregation === 'daily' && selectedMonth !== null) {
      const year        = selectedMonth <= 2 ? localDate(toDate).getFullYear() : localDate(fromDate).getFullYear();
      const mm          = String(selectedMonth + 1).padStart(2, '0');
      const daysInMonth = new Date(year, selectedMonth + 1, 0).getDate();
      return { fromDate: `${year}-${mm}-01`, toDate: `${year}-${mm}-${String(daysInMonth).padStart(2, '0')}` };
    }
    const max = AGG_MAX_DAYS[aggregation];
    if (!max || !fromDate || !toDate) return { fromDate, toDate };
    const cap = new Date(fromDate + 'T00:00:00');
    cap.setDate(cap.getDate() + max - 1);
    const capStr = cap.toISOString().slice(0, 10);
    return { fromDate, toDate: toDate > capStr ? capStr : toDate };
  }, [aggregation, selectedMonth, fromDate, toDate]); // eslint-disable-line

  const isCapped = (aggregation === 'daily' && selectedMonth !== null) ? false : effectiveDates.toDate !== toDate;

  const [selectedWeekMonths, setSelectedWeekMonths] = useState(null);
  const [selectedQuarters,   setSelectedQuarters]   = useState(null);
  const [selectedHalves,     setSelectedHalves]     = useState(null);

  useEffect(() => { setSelectedWeekMonths(null); setSelectedQuarters(null); setSelectedHalves(null); }, [aggregation]);

  return { fromDate, setFromDate, toDate, setToDate, aggregation, setAggregation, selectedMonth, setSelectedMonth,
    effectiveDates, isCapped, selectedWeekMonths, setSelectedWeekMonths, selectedQuarters, setSelectedQuarters,
    selectedHalves, setSelectedHalves };
}

// ─── FYMultiSelect — financial year multi-select (used only by IntensityFilterBar) ──

const fyShortLabel = fy => {
  const parts = (fy.financial_year_value || '').split('-');
  return parts.length >= 2 ? `${parts[0]}-${parts[1].slice(2)}` : fy.financial_year_value;
};

function FYMultiSelect({ fyList, selectedFYIds, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const sorted    = [...fyList].sort((a, b) => parseInt(b.financial_year_value) - parseInt(a.financial_year_value));
  const allSel    = fyList.length > 0 && selectedFYIds.length === fyList.length;
  const toggle    = id => onChange(selectedFYIds.includes(id) ? selectedFYIds.filter(x => x !== id) : [...selectedFYIds, id]);
  const toggleAll = () => onChange(allSel ? [] : fyList.map(f => String(f.id)));
  const display   = selectedFYIds.length === 0 ? 'None'
    : allSel ? `All (${fyList.length})`
    : selectedFYIds.length === 1 ? fyShortLabel(fyList.find(f => String(f.id) === selectedFYIds[0]) || {})
    : `${selectedFYIds.length} FYs`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={S.filterLabel}>📆 Financial Year</div>
      <div style={{ ...S.trigger, minWidth: 140, borderColor: open ? '#3b82f6' : '#e5e7eb' }} onClick={() => setOpen(o => !o)}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
        <span style={{ fontSize: 9, color: '#9ca3af', marginLeft: 6, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={S.dropdown}>
          <div style={S.dropdownAll} onClick={toggleAll}>
            <input type="checkbox" readOnly checked={allSel} /><span style={{ fontWeight: 600 }}>Select All</span>
          </div>
          {sorted.map(fy => {
            const id      = String(fy.id);
            const checked = selectedFYIds.includes(id);
            return (
              <div key={id} style={{ ...S.dropdownOpt, background: checked ? '#eff6ff' : 'transparent' }} onClick={() => toggle(id)}>
                <input type="checkbox" readOnly checked={checked} /><span>{fyShortLabel(fy)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── IntensityFilterBar — shared filter UI used by both pages ─────────────────

export function IntensityFilterBar({ filters, data, totalRows, periodCount, allPeriods = [], tabToggle, graphAxes, fySelector }) {
  const { fromDate, setFromDate, toDate, setToDate, aggregation, setAggregation, selectedMonth, setSelectedMonth, isCapped,
    selectedWeekMonths, setSelectedWeekMonths, selectedQuarters, setSelectedQuarters, selectedHalves, setSelectedHalves } = filters;
  const { locations, selectedLocIds, setSelectedLocIds, allConfigs, selectedConfigIds, setSelectedConfigIds,
    kpiOptions, selectedKpis, setSelectedKpis, denomData = [], selectedDenomIds = [], setSelectedDenomIds } = data;

  const smartDefault  = graphAxes?.limitMax != null ? Math.min(10, graphAxes.limitMax) : 10;
  const [limitInput, setLimitInput] = useState(graphAxes?.limit ?? smartDefault);
  useEffect(() => { setLimitInput(graphAxes?.limit ?? smartDefault); }, [graphAxes?.limit]); // eslint-disable-line
  useEffect(() => {
    if (!graphAxes) return;
    const id = setTimeout(() => {
      const n = limitInput === '' ? null : parseInt(limitInput, 10);
      if (n === graphAxes.limit) return;
      graphAxes.onLimitChange(!n || n < 1 ? null : (graphAxes.limitMax ? Math.min(n, graphAxes.limitMax) : n));
    }, 400);
    return () => clearTimeout(id);
  }, [limitInput]); // eslint-disable-line

  const fyBounds = fySelector?.selectedFYIds?.length
    ? (() => {
        const ranges = fySelector.fyList
          .filter(f => fySelector.selectedFYIds.includes(String(f.id)))
          .map(fy => fyDateRange(fy, fySelector.startingMonth));
        return {
          fromDate: [...ranges.map(r => r.fromDate)].sort()[0],
          toDate:   [...ranges.map(r => r.toDate)].sort().reverse()[0],
        };
      })()
    : null;
  const dateMin = fyBounds?.fromDate || '';
  const dateMax = fyBounds?.toDate   || getToday();

  return (
    <div style={S.filterCard}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 2 }}>
        <div>
          <div style={S.filterCardTitle}>📊 Data Filters</div>
          <div style={S.filterCardSubtitle}>Configure your data selection criteria</div>
        </div>
        {tabToggle && <div style={{ flexShrink: 0, marginLeft: 12 }}>{tabToggle}</div>}
      </div>
      <div style={S.filtersRow}>
        {fySelector && fySelector.fyList.length > 0 && (
          <FYMultiSelect fyList={fySelector.fyList} selectedFYIds={fySelector.selectedFYIds} onChange={fySelector.onFYChange} />
        )}
        <div>
          <div style={S.filterLabel}>📅 From Date</div>
          <input type="date" style={{ ...S.select, minWidth: 140 }} value={fromDate} min={dateMin} max={toDate || dateMax} onChange={e => setFromDate(e.target.value)} />
        </div>
        <div>
          <div style={S.filterLabel}>To Date</div>
          <input type="date" style={{ ...S.select, minWidth: 140 }} value={toDate} min={fromDate} max={dateMax} onChange={e => setToDate(e.target.value)} />
        </div>
        <LocationMultiSelect locations={locations} selected={selectedLocIds} onChange={setSelectedLocIds} />
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <div style={S.filterLabel}>📐 Aggregate</div>
            <select style={S.select} value={aggregation} onChange={e => { setAggregation(e.target.value); if (e.target.value !== 'daily') setSelectedMonth(null); }}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="half-yearly">Half-Yearly</option>
            </select>
          </div>
          {aggregation === 'daily' && (
            <div>
              <div style={S.filterLabel}>Month</div>
              <select style={S.select} value={selectedMonth ?? ''} onChange={e => setSelectedMonth(e.target.value === '' ? null : parseInt(e.target.value, 10))}>
                <option value="">Select month</option>
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          )}
          {aggregation === 'weekly' && allPeriods.length > 0 && (() => {
            const avail = [...new Set(allPeriods.map(p => new Date(p.fromDate + 'T00:00:00').getMonth()))].sort((a, b) => a - b);
            const sel   = selectedWeekMonths ?? avail;
            return (
              <MultiSelect label="Months" options={avail.map(m => ({ id: m, label: MONTHS_SHORT[m] }))} selected={sel}
                onChange={v => setSelectedWeekMonths(v.length === avail.length ? null : v)} getId={o => o.id} getLabel={o => o.label} />
            );
          })()}
          {aggregation === 'quarterly' && allPeriods.length > 0 && (() => {
            const avail = [...new Set(allPeriods.map(p => { const m = p.label.match(/Q(\d)/); return m ? parseInt(m[1]) : null; }).filter(Boolean))].sort();
            const sel   = selectedQuarters ?? avail;
            return (
              <MultiSelect label="Quarters" options={avail.map(q => ({ id: q, label: `Q${q}` }))} selected={sel}
                onChange={v => setSelectedQuarters(v.length === avail.length ? null : v)} getId={o => o.id} getLabel={o => o.label} />
            );
          })()}
          {aggregation === 'half-yearly' && allPeriods.length > 0 && (() => {
            const avail = [...new Set(allPeriods.map(p => p.label.slice(0, 2)))];
            const sel   = selectedHalves ?? avail;
            return (
              <div>
                <div style={S.filterLabel}>Half</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {avail.map(h => {
                    const active = sel.includes(h);
                    return (
                      <button key={h}
                        onClick={() => {
                          const next = active ? sel.filter(x => x !== h) : [...sel, h];
                          setSelectedHalves(next.length === 0 ? avail : next.length === avail.length ? null : next);
                        }}
                        style={{ padding: '5px 14px', borderRadius: 6, border: '1.5px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          background: active ? '#3f88a7' : '#fff', color: active ? '#fff' : '#374151',
                          borderColor: active ? '#3f88a7' : '#d1d5db', transition: 'all 0.15s' }}
                      >{h}</button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
        <MultiSelect label="Top Level" options={allConfigs} selected={selectedConfigIds} getId={c => c.id} getLabel={c => cfgLabel(c)}
          onChange={newIds => {
            const removed = selectedConfigIds.filter(id => !newIds.includes(id));
            if (removed.length > 0) {
              const kpisToRemove = new Set(removed.flatMap(id => allConfigs.find(c => c.id === id)?.kpiLevel?.kpiIds || []));
              setSelectedKpis(prev => prev.filter(k => !kpisToRemove.has(k)));
            }
            setSelectedConfigIds(newIds);
          }}
        />
        {kpiOptions.length > 0 && (
          <MultiSelect label="KPI Level" options={kpiOptions} selected={selectedKpis} onChange={setSelectedKpis} getId={o => o.id} getLabel={o => o.label} />
        )}
        {denomData.length > 0 && setSelectedDenomIds && (
          <MultiSelect label="Denominators" options={denomData} selected={selectedDenomIds} onChange={setSelectedDenomIds} getId={d => d.title} getLabel={d => d.title} />
        )}
      </div>
      {graphAxes && (
        <>
          <div style={{ height: 1, background: '#e5e7eb', margin: '14px 0' }} />
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>→ Graph Axes</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={S.filterLabel}>X-Axis</div>
              <select style={S.select} value={graphAxes.xAxis} onChange={e => graphAxes.onXAxisChange(e.target.value)}>
                <option value="period">Period</option>
                <option value="financial-year">Financial Year</option>
                <option value="location">Location</option>
              </select>
            </div>
            <div>
              <div style={S.filterLabel}>Y-Axis (Series)</div>
              <select style={S.select} value={graphAxes.yAxis} onChange={e => graphAxes.onYAxisChange(e.target.value)}>
                <option value="none">None (aggregated)</option>
                <option value="financial-year" disabled={graphAxes.xAxis === 'financial-year'}>Financial Year</option>
                <option value="location" disabled={graphAxes.xAxis === 'location'}>Location</option>
              </select>
            </div>
            <div>
              <div style={S.filterLabel}>Sort By</div>
              <select style={{ ...S.select, opacity: graphAxes.yAxis !== 'none' ? 0.4 : 1, cursor: graphAxes.yAxis !== 'none' ? 'not-allowed' : 'default' }}
                value={graphAxes.sortBy} onChange={e => graphAxes.onSortByChange(e.target.value)} disabled={graphAxes.yAxis !== 'none'}>
                <option value="value-desc">Value (high → low)</option>
                <option value="value-asc">Value (low → high)</option>
                <option value="name-asc">Name (A → Z)</option>
              </select>
            </div>
            <div>
              <div style={S.filterLabel}>Chart Type</div>
              <select style={S.select} value={graphAxes.chartOverride} onChange={e => graphAxes.onChartTypeChange(e.target.value)}>
                <option value="line">Line</option>
                <option value="area">Area</option>
                <option value="column">Column</option>
                <option value="bar">Bar (horizontal)</option>
                <option value="pie">Pie</option>
                <option value="donut">Donut</option>
                <option value="radar">Radar</option>
              </select>
            </div>
            <div>
              <div style={S.filterLabel}>Limit</div>
              <input type="number" min="1" max={graphAxes.limitMax ?? undefined} placeholder="All"
                style={{ ...S.select, width: 72 }} value={limitInput} onChange={e => setLimitInput(e.target.value)} />
            </div>
          </div>
        </>
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ ...S.statBadge, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>{totalRows} Intensity Types</span>
        <span style={{ ...S.statBadge, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>{periodCount} Periods</span>
        <span style={{ ...S.statBadge, background: '#fdf4ff', color: '#7e22ce', border: '1px solid #e9d5ff' }}>{selectedDenomIds.length} / {denomData.length} Denominators</span>
        <span style={{ ...S.statBadge, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}>{selectedLocIds.length} / {locations.length} Locations</span>
        {graphAxes && (
          <>
            <span style={{ ...S.statBadge, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}>X: {{ 'period': 'Period', 'financial-year': 'Financial Year', 'location': 'Location' }[graphAxes.xAxis]}</span>
            <span style={{ ...S.statBadge, background: '#fdf4ff', color: '#7e22ce', border: '1px solid #e9d5ff' }}>Y: {{ 'none': 'None (aggregated)', 'financial-year': 'Financial Year', 'location': 'Location' }[graphAxes.yAxis]}</span>
          </>
        )}
        {isCapped && (
          <span style={{ ...S.statBadge, background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d' }}>
            ⚠ {aggregation === 'daily' ? 'Max 30 days' : 'Max 12 weeks'} shown from {fromDate}
          </span>
        )}
      </div>
    </div>
  );
}
